import { Navigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';

import { modeHomeFor } from './mode-home';
import NoWorkspacePage from './NoWorkspacePage';
import { useAppMode } from './use-app-mode';

/**
 * Mode-aware landing for `/`.
 *
 * Admin mode opens on the Overview when the viewer can read it, and on the
 * first admin page they can open when they cannot — `/admin/overview` is gated
 * on `read:dashboards`, which CSR, Finance and Marketing do not hold, so a
 * fixed route would just move the Unauthorized page rather than remove it.
 * Teacher mode has one home. `modeHomeFor` owns both answers.
 *
 * Mode and permissions both resolve off the one shared access-token decode
 * (`useTokenClaims`), so the two loading flags settle together — waiting on
 * both costs nothing and guarantees the `replace` navigation never fires on a
 * viewer whose grants have not been read yet. Firing early stranded a CSR on
 * the catch-all: this component unmounts once the target is reached, so there
 * is no second chance to re-navigate.
 *
 * Nothing here waits on `/staff/me` any more. Landing used to be decided by the
 * viewer's region, which only the profile knows; mode is decided by grants that
 * arrive with the token, so the redirect no longer has a slower input than the
 * one it already waits for.
 *
 * A viewer granted NEITHER mode has nowhere to be sent — both trees refuse
 * them, and this component is where Unauthorized's "return to home" leads, so
 * any redirect loops. They get `NoWorkspacePage`, which names the usual cause.
 */
export default function HomeRedirect() {
  const viewer = useAppMode();
  const { hasAnyPermission, loading: permsLoading } = usePermissions();

  if (viewer.loading || permsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="w-8 h-8 border-2 border-moss-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // No mode grant at all: both trees would refuse them, and Unauthorized's way
  // home is this component — so redirecting anywhere is a loop. Say so instead.
  if (viewer.availableModes.length === 0) return <NoWorkspacePage />;

  return <Navigate to={modeHomeFor(viewer.mode, hasAnyPermission)} replace />;
}
